import { useMutation, useQuery } from '@tanstack/react-query';

import type { paths } from '@luxuryescapes/contract-svc-reservation/server';

import { LUXURY_ESCAPES } from '~/consts/brands';

import ReservationService from '~/services/ReservationService';
import UsersService from '~/services/UsersService';

export type RentalImportCommitResult =
  paths['/api/properties/rental-import/commit']['post']['responses'][200]['content']['application/json']['result'];

export type RentalImportStatusItem =
  paths['/api/properties/rental-import/{batchId}']['get']['responses'][200]['content']['application/json']['result'];

export type RentalImportBatchSummary =
  paths['/api/properties/rental-import/batches']['get']['responses'][200]['content']['application/json']['result']['batches'][number];

export type RentalImportRow = RentalImportStatusItem['results'][number];

export type RentalImportState = RentalImportStatusItem['state'];

export function useCommitRentalImport() {
  return useMutation({
    mutationFn: ({
      file,
      commission,
    }: {
      file: File;
      commission: number;
    }): Promise<{ result: RentalImportCommitResult }> => ReservationService.commitBulkRentalImport(file, commission),
  });
}

export function useRentalImportStatus(batchId: string | null) {
  return useQuery({
    queryKey: ['vendors', 'rental-import', batchId],
    queryFn: (): Promise<{ result: RentalImportStatusItem }> => ReservationService.getBulkRentalImportStatus(batchId),
    enabled: !!batchId,
    retry: false,
    refetchInterval: (query) => {
      if (query.state.error) {
        return false;
      }
      const state = query.state.data?.result.state;
      return state === 'completed' || state === 'failed' ? false : 1200;
    },
  });
}

export function useListRentalImportBatches(enabled = true) {
  return useQuery({
    queryKey: ['vendors', 'rental-import', 'batches'],
    queryFn: async (): Promise<Array<RentalImportBatchSummary>> => {
      const response = (await ReservationService.listBulkRentalImportBatches()) as {
        result: { batches: Array<RentalImportBatchSummary> };
      };
      return response.result.batches;
    },
    enabled,
  });
}

const USER_LOOKUP_CHUNK_SIZE = 15;

export function chunkUserIds(ids: Array<string>, size = USER_LOOKUP_CHUNK_SIZE): Array<Array<string>> {
  const chunks: Array<Array<string>> = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}

export async function fetchBatchUsers(
  uniqueIds: Array<string>,
  fetchChunk: (ids: Array<string>) => Promise<Map<string, App.UserSummary>>,
): Promise<Map<string, App.UserSummary>> {
  const results = await Promise.all(
    chunkUserIds(uniqueIds).map((chunk) =>
      fetchChunk(chunk).catch((error) => {
        console.error('Failed to load some batch history users', error);
        return new Map<string, App.UserSummary>();
      }),
    ),
  );

  return new Map(results.flatMap((chunkResult) => Array.from(chunkResult.entries())));
}

export function useRentalImportBatchUsers(userIds: Array<string>) {
  const uniqueIds = Array.from(new Set(userIds)).sort();

  return useQuery({
    queryKey: ['vendors', 'rental-import', 'batch-users', uniqueIds],
    queryFn: (): Promise<Map<string, App.UserSummary>> =>
      fetchBatchUsers(uniqueIds, (chunk) => UsersService.getUsersSummaryByIds(chunk, LUXURY_ESCAPES)),
    enabled: uniqueIds.length > 0,
  });
}

export function useRetryRentalImportRow() {
  return useMutation({
    mutationFn: ({
      batchId,
      rowNumber,
    }: {
      batchId: string;
      rowNumber: number;
    }): Promise<{ result: RentalImportStatusItem }> => ReservationService.retryBulkRentalImportRow(batchId, rowNumber),
  });
}
